import { useRouteExtensions } from '@/use/use-route-extensions';
import leadAanvraagApi from '@/api/lead-aanvraag.api';
import { useAanbodExtensions } from '@/use/aanbod-definitie/use-aanbod-extensions';
import { useLeadAanvraagStore } from '@/stores/lead-aanvraag/lead-aanvraag';
import router from '@/router';
import { computed } from 'vue';
import { useIsFetching } from '@tanstack/vue-query';
import SubflowStatusEnum from '@/stores/lead-aanvraag/SubflowStatus.enum.js';
import { useFlowExtensions } from '@/use/use-flow-extensions';
import BetaaloptieEnum from '@/stores/lead-aanvraag/Betaaloptie.enum';
import aanvraagApi from '@/api/aanvraag.api';
import { useRootStore } from '@/stores/index';
import betalingApi from '@/api/betaling.api';
import { useStartAanbod } from './use-start-aanbod';

export const useFlowNavigation = (overrideGetAanbodDefinitieByNaam) => {
  const { aanbodDefinitie } = useAanbodExtensions(overrideGetAanbodDefinitieByNaam);
  const isFetching = useIsFetching();
  const flowExt = useFlowExtensions();
  const { startAanbod } = useStartAanbod();
  async function navigateToSubflow(subflow) {
    const leadAanvraagStore = useLeadAanvraagStore();
    const { aanbodNaam } = useRouteExtensions();
    const subflowState = leadAanvraagStore.leadAanvraag.subflowState.find(
      (sf) => sf.subflowNaam == subflow.naam
    );
    let subflowNaam = subflow.naam;
    let pageNaam;
    //BACK-COMPAT
    let subflowHasPages = subflow.pages != undefined || null;

    if (subflowState.subflowStatus == SubflowStatusEnum.Finished) {
      if (subflowHasPages) {
        pageNaam = subflow.pages[subflow.pages.length - 1].naam;
      } else {
        pageNaam = subflow.stappen[subflow.stappen.length - 1].naam;
      }
    } else if (subflowState.subflowStatus == SubflowStatusEnum.Skipped) {
      if (subflowHasPages) {
        pageNaam = subflow.pages[0].naam;
      } else {
        pageNaam = subflow.stappen[0]?.naam;
      }
    } else {
      const nextPagePointer = await leadAanvraagApi.getNextPage(
        leadAanvraagStore.leadAanvraag.aanvraagId,
        aanbodNaam.value,
        subflow.naam
      );

      pageNaam = nextPagePointer.page;
      subflowNaam = nextPagePointer.subflow;
    }

    navigateToSubflowPage(subflowNaam, pageNaam);
  }

  async function navigateToSubflowPage(subflowNaam, pageNaam, aanbodNaam, aanvraagId) {
    const {
      lang,
      aanbodNaam: currentAanbodNaam,
      aanvraagId: currentAanvraagId
    } = useRouteExtensions();

    await router.push(
      `/${lang.value}/${
        aanbodNaam ?? currentAanbodNaam.value
      }/${subflowNaam}/${pageNaam}?aanvraagId=${aanvraagId ?? currentAanvraagId.value}`
    );
  }

  const loading = computed(() => {
    const leadAanvraagStore = useLeadAanvraagStore();
    return isFetching.value > 0 || leadAanvraagStore.loading;
  });

  const initialStateIsLoading = computed(() => {
    const leadAanvraagStore = useLeadAanvraagStore();
    return isFetching.value > 0 || !leadAanvraagStore.leadAanvraag;
  });

  async function navigateToFirstPageOfFirstSubflow() {
    const leadAanvraagStore = useLeadAanvraagStore();
    const routeExtensions = useRouteExtensions();

    let lang = routeExtensions.lang.value;
    let aanbod = routeExtensions?.aanbodNaam.value;
    const subflow = aanbodDefinitie.value.subflows[0];
    let page;
    page = subflow?.pages[0];

    const params = {
      lang: lang,
      aanbod: aanbod,
      subflow: subflow.naam,
      page: page.naam
    };

    const nextPage = {
      name: 'Page',
      params: params,
      query: {
        aanvraagId: leadAanvraagStore.leadAanvraag.aanvraagId ?? routeExtensions.aanvraagId.value
      }
    };

    await router.push(nextPage);
  }

  async function finalize({
    recaptchaToken,
    aanvaardeJuridischeDocumentVersieIds,
    betaalOptie,
    aanvaardeQueenRecapCheckboxes
  }) {
    const leadAanvraagStore = useLeadAanvraagStore();

    if (leadAanvraagStore.finalizePending) return;
    leadAanvraagStore.finalizePending = true;

    const rootStore = useRootStore();

    leadAanvraagStore.setValueOnLeadAanvraag({
      contentLocator: 'metrics.finalizedOnMobile',
      value: rootStore.isMobile
    });

    leadAanvraagStore.setValueOnLeadAanvraag({
      contentLocator: 'aanvaardeJuridischeDocumentVersieIds',
      value: aanvaardeJuridischeDocumentVersieIds
    });

    leadAanvraagStore.setValueOnLeadAanvraag({
      contentLocator: 'AanvaardeQueenRecapCheckboxes',
      value: aanvaardeQueenRecapCheckboxes
    });

    if (flowExt.totaalPrijs.value !== 0 && !betaalOptie) {
      betaalOptie = BetaaloptieEnum.Online;
    }

    leadAanvraagStore.setValueOnLeadAanvraag({
      contentLocator: 'GekozenBetaaloptie',
      value: betaalOptie
    });

    const routeExtensions = useRouteExtensions();

    await leadAanvraagApi.putLeadAanvraag(
      {
        aanbod: routeExtensions.currentRoute.value.params.aanbod,
        lang: routeExtensions.currentRoute.value.params.lang
      },
      leadAanvraagStore.leadAanvraag
    );

    if (flowExt.totaalPrijs.value === 0 || +betaalOptie === BetaaloptieEnum.Overschrijving) {
      const result = await aanvraagApi.createAanvraag(
        routeExtensions.aanvraagId.value,
        routeExtensions.aanbodNaam.value,
        recaptchaToken
      );
      window.location = result.headers.location;
      return;
    }

    let aanbod = routeExtensions.currentRoute.value.params.aanbod;
    let aanvraagId = routeExtensions.currentRoute.value.query.aanvraagId;

    betalingApi.createBetaling(aanbod, aanvraagId).then((data) => {
      if (data.routeName) {
        routeExtensions.pushToRouter({ name: data.routeName, query: data.query });
      } else {
        window.location = data.redirectUrl;
      }
    });

    leadAanvraagStore.finalizePending = false;
  }

  return {
    loading,
    startAanbod,
    navigateToFirstPageOfFirstSubflow,
    initialStateIsLoading,
    navigateToSubflow,
    navigateToSubflowPage,
    finalize
  };
};
